import { AnalyticsUserEvent } from '@fruitful/types';
import { Link, router } from 'expo-router';
import React, { useCallback, useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Image,
    isNonMobileWeb,
    View,
    YStack,
} from 'smoothie';

import QRCode from '../../../../assets/images/auth/appleAppStoreQRCode.png';
import AppStorePrompt from '../../../../assets/images/auth/downloadOnTheAppStore.svg';
import BackgroundImage from '../../../../assets/images/backgrounds/app-on-iphone.png';
import MobileAppPreviewImage from '../../../../assets/images/backgrounds/app-on-iphone-mobile.png';
import {
    AppleDisclosuresFootnote,
    GeneralDisclosuresFootnote,
} from '../../../banking/components/Disclosures';
import { useFullBleedMobileWebBackground } from '../../../common/hooks/useFullBleedMobileWebBackground';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { track } from '../../../logger/Logger';
import { APPLE_APP_STORE_URL } from '../../constants';
import { NewMemberScreenLayout } from '../new-member/NewMemberScreenLayout';
import { SplitScreenHighlightLayout } from './SplitScreenHighlightLayout';

export const shouldShowIOSAppPage = () => {
    // We want to show this page only if:
    // a) the device is an iPhone (definitely has an iOS device)
    // b) the device is a non-mobile web screen (might have an iOS device)
    // otherwise, the device is likely a non-iOS mobile device (likely does not have an iOS device)
    const isIPhoneUserAgent = window?.navigator.userAgent
        .toLowerCase()
        .includes('iphone');
    return isIPhoneUserAgent || isNonMobileWeb;
};

export const GetTheApp = () => {
    const { trackViewOnce } = useViewEvents();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [downloadHasBeenClicked, setDownloadHasBeenClicked] = useState(false);

    trackViewOnce('GetTheApp');

    const onDownloadClicked = useCallback(() => {
        track(AnalyticsUserEvent.SIGNUP_FUNNEL_DOWNLOAD_APP_PRESSED);
        setDownloadHasBeenClicked(true);
    }, []);

    const goToNextPage = useCallback(() => {
        track(AnalyticsUserEvent.SIGNUP_FUNNEL_GET_APP_PROMPT, {
            downloadClicked: downloadHasBeenClicked,
        });
        setIsSubmitting(true);
        router.push('/home');
    }, [router, downloadHasBeenClicked]);

    useFullBleedMobileWebBackground();

    if (!isNonMobileWeb) {
        return (
            <NewMemberScreenLayout title="Get the Fruitful app">
                <Image
                    source={MobileAppPreviewImage}
                    alt=""
                    width={400}
                    height={318}
                    alignSelf="center"
                    objectFit="contain"
                    resizeMode="contain"
                />

                <YStack alignItems="center" gap={16}>
                    <Link href={APPLE_APP_STORE_URL} target="_blank">
                        <View onPress={onDownloadClicked}>
                            <AppStorePrompt height={40} width={120} />
                        </View>
                    </Link>
                    {!downloadHasBeenClicked && (
                        <BodyMediumRegular textAlign="center">
                            or
                        </BodyMediumRegular>
                    )}
                    <Button
                        variant={
                            downloadHasBeenClicked
                                ? ButtonVariant.PRIMARY
                                : ButtonVariant.SECONDARY
                        }
                        size={ButtonSize.LARGE}
                        disabled={isSubmitting}
                        onPress={goToNextPage}
                        text={
                            downloadHasBeenClicked
                                ? 'Continue'
                                : 'Continue on web'
                        }
                    />
                </YStack>
            </NewMemberScreenLayout>
        );
    }

    const title = 'Get the App';
    const subtitle = isNonMobileWeb
        ? 'Scan to download the Fruitful iOS app to book your first session and get started.'
        : 'Download the Fruitful iOS app to book your first session and get started.';

    return (
        <SplitScreenHighlightLayout
            backgroundImage={BackgroundImage}
            title={title}
            subtitle={subtitle}
            disclosures={<Disclosures />}
        >
            <AppleAppStoreDownload onDownloadClick={onDownloadClicked} />
            <View width={isNonMobileWeb ? '80%' : '100%'} alignSelf="center">
                <YStack
                    alignItems="center"
                    marginTop={isNonMobileWeb ? 48 : 32}
                    gap={16}
                >
                    {!downloadHasBeenClicked && (
                        <BodyMediumRegular textAlign="center" marginTop={-20}>
                            or
                        </BodyMediumRegular>
                    )}
                    <Button
                        variant={
                            downloadHasBeenClicked
                                ? ButtonVariant.PRIMARY
                                : ButtonVariant.SECONDARY
                        }
                        size={ButtonSize.LARGE}
                        disabled={isSubmitting}
                        onPress={goToNextPage}
                        text={
                            downloadHasBeenClicked
                                ? 'Continue'
                                : 'Continue on web'
                        }
                    />
                </YStack>
            </View>
        </SplitScreenHighlightLayout>
    );
};

const AppleAppStoreDownload = ({
    onDownloadClick,
}: {
    onDownloadClick: () => void;
}) => {
    const [downloadButtonVariant, setDownloadButtonVariant] =
        useState<ButtonVariant>(ButtonVariant.PRIMARY);

    return (
        <View width="100%">
            {isNonMobileWeb ? (
                <YStack justifyContent="center" alignItems="center" gap={16}>
                    <View
                        borderRadius={24}
                        padding={12}
                        backgroundColor="$white"
                    >
                        <Image
                            source={QRCode}
                            alt=""
                            width={300}
                            height={300}
                            objectFit="contain"
                            resizeMode="contain"
                        />
                    </View>
                    <Link href={APPLE_APP_STORE_URL} target="_blank">
                        <View onPress={onDownloadClick}>
                            <AppStorePrompt height={40} width={120} />
                        </View>
                    </Link>
                </YStack>
            ) : (
                <YStack justifyContent="center" alignItems="center">
                    <Link
                        href={APPLE_APP_STORE_URL}
                        target="_blank"
                        style={{ display: 'flex', width: '100%' }}
                    >
                        <Button
                            variant={downloadButtonVariant}
                            onPress={() => {
                                setDownloadButtonVariant(
                                    ButtonVariant.TERTIARY,
                                );
                                onDownloadClick();
                            }}
                            size={ButtonSize.LARGE}
                            text="Download"
                        />
                    </Link>
                </YStack>
            )}
        </View>
    );
};

const Disclosures = () => {
    return (
        <YStack
            paddingTop={64}
            width="98%"
            maxWidth={600}
            alignSelf="center"
            userSelect="none"
            gap={8}
        >
            <GeneralDisclosuresFootnote />
            <AppleDisclosuresFootnote />
        </YStack>
    );
};
